import { create } from "zustand";
import { persist } from "zustand/middleware";
import {
  signIn,
  confirmSignIn as cognitoConfirmSignIn,
  signUp as cognitoSignUp,
  confirmSignUp as cognitoConfirmSignUp,
  resendSignUpCode as cognitoResendSignUpCode,
  resetPassword as cognitoResetPassword,
  confirmResetPassword as cognitoConfirmResetPassword,
  signOut as cognitoSignOut,
  getCurrentUser,
  fetchAuthSession,
} from "aws-amplify/auth";
import { useUserApplicationsStore } from "@/stores/userApplicationsStore";
export interface User {
  id: string;
  username: string;
  email?: string;
  displayName?: string;
  groups?: string[]; // Cognito user groups (permissions)
  attributes?: Record<string, any>;
}

const SIGNUP_USERNAME_STORAGE_KEY = "signup-username";

const buildSignupUsername = (firstName: string, lastName: string) => {
  const base = `${firstName}_${lastName}`.toLowerCase().replace(/[^a-z0-9_]/g, "");
  return base
};

const getStoredSignupUsername = () => {
  if (typeof window === "undefined") return "";
  return localStorage.getItem(SIGNUP_USERNAME_STORAGE_KEY) || "";
};

const storeSignupUsername = (username: string) => {
  if (typeof window === "undefined") return;
  localStorage.setItem(SIGNUP_USERNAME_STORAGE_KEY, username);
};

interface AuthState {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  permissions: string[]; // Array of permissions/groups
  setUser: (user: User | null) => void;
  signIn: (username: string, password: string) => Promise<void>;
  completeNewPassword: (newPassword: string) => Promise<void>;
  signUp: (email: string, password: string, firstName: string, lastName: string) => Promise<void>;
  confirmSignUp: (email: string, confirmationCode: string) => Promise<void>;
  resendSignUp: (email: string) => Promise<void>;
  resetPassword: (email: string) => Promise<void>;
  confirmResetPassword: (email: string, code: string, newPassword: string) => Promise<void>;
  signOut: () => Promise<void>;
  checkAuth: () => Promise<void>;
  hasPermission: (permission: string) => boolean;
  hasAnyPermission: (permissions: string[]) => boolean;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      user: null,
      isAuthenticated: false,
      isLoading: false,
      permissions: [],

      setUser: (user) => {
        const permissions = user?.groups || [];
        set({ 
          user, 
          isAuthenticated: !!user,
          permissions,
        });
      },

      signIn: async (username: string, password: string) => {
        set({ isLoading: true });
        try {
          const result = await signIn({ username, password });
          if (result.nextStep?.signInStep === "CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED") {
            const challengeError = new Error("A new password is required before you can sign in.");
            (challengeError as Error & { name: string }).name = "NEW_PASSWORD_REQUIRED";
            throw challengeError;
          }
          // After successful sign in, check auth to get user details
          await get().checkAuth();
        } catch (error: any) {
          console.error("Sign in error:", error);
          throw error;
        } finally {
          set({ isLoading: false });
        }
      },

      completeNewPassword: async (newPassword: string) => {
        set({ isLoading: true });
        try {
          await cognitoConfirmSignIn({ challengeResponse: newPassword });
          await get().checkAuth();
        } catch (error: any) {
          console.error("Complete new password challenge error:", error);
          throw error;
        } finally {
          set({ isLoading: false });
        }
      },

      signUp: async (email: string, password: string, firstName: string, lastName: string) => {
        set({ isLoading: true });
        try {
          const username = buildSignupUsername(firstName, lastName);
          await cognitoSignUp({
            username,
            password,
            options: {
              userAttributes: {
                email,
                middle_name: lastName,
                name: `${firstName} ${lastName}`.trim(),
              },
            },
          });
          storeSignupUsername(username);
        } catch (error: any) {
          console.error("Sign up error:", error);
          throw error;
        } finally {
          set({ isLoading: false });
        }
      },

      confirmSignUp: async (email: string, confirmationCode: string) => {
        set({ isLoading: true });
        try {
          const storedUsername = getStoredSignupUsername();
          await cognitoConfirmSignUp({
            username: storedUsername || email,
            confirmationCode,
            options: {
              forceAliasCreation: false,
            },
          });
        } catch (error: any) {
          console.error("Confirm sign up error:", error);
          throw error;
        } finally {
          set({ isLoading: false });
        }
      },

      resendSignUp: async (email: string) => {
        set({ isLoading: true });
        try {
          const storedUsername = getStoredSignupUsername();
          await cognitoResendSignUpCode({ username: storedUsername || email });
        } catch (error: any) {
          console.error("Resend sign up error:", error);
          throw error;
        } finally {
          set({ isLoading: false });
        }
      },

      resetPassword: async (email: string) => {
        set({ isLoading: true });
        try {
          await cognitoResetPassword({ username: email });
        } catch (error: any) {
          console.error("Reset password error:", error);
          throw error;
        } finally {
          set({ isLoading: false });
        }
      },

      confirmResetPassword: async (email: string, code: string, newPassword: string) => {
        set({ isLoading: true });
        try {
          await cognitoConfirmResetPassword({ username: email, confirmationCode: code, newPassword });
        } catch (error: any) {
          console.error("Confirm reset password error:", error);
          throw error;
        } finally {
          set({ isLoading: false });
        }
      },

      signOut: async () => {
        try {
          await cognitoSignOut();
          window.location.href = "/auth/login";
          window.location.reload();
        } catch (error) {
          console.error("Sign out error:", error);
        } finally {
          set({ 
            user: null, 
            isAuthenticated: false,
            permissions: [],
          });
          // Clear all local storage (auth tokens, persisted state, etc.)
          localStorage.clear();
          useUserApplicationsStore.getState().clearApplications();
        }
      },

      checkAuth: async () => {
        set({ isLoading: true });
        try {
          const user = await getCurrentUser();
          const session = await fetchAuthSession();
          
          // Extract user attributes
          const attributes: Record<string, any> = {};
          if (session.tokens?.idToken?.payload) {
            const payload = session.tokens.idToken.payload as any;
            attributes.email = payload.email || payload["cognito:username"];
            attributes.groups = payload["cognito:groups"] || [];
            attributes.name = payload.name;
            attributes.given_name = payload.given_name;
            attributes.family_name = payload.family_name;
          }

          // Get groups from token claims
          const groups = attributes.groups || [];
          
          const displayName =
            attributes.name ||
            [attributes.given_name, attributes.family_name].filter(Boolean).join(" ").trim() ||
            attributes.email ||
            user.username;

          const userData: User = {
            id: user.userId,
            username: user.username,
            email: attributes.email,
            displayName,
            groups,
            attributes,
          };

          set({ 
            user: userData,
            isAuthenticated: true,
            permissions: groups,
          });
        } catch (error) {
          console.error("Auth check error:", error);
          set({ 
            user: null, 
            isAuthenticated: false,
            permissions: [],
          });
        } finally {
          set({ isLoading: false });
        }
      },

      hasPermission: (permission: string) => {
        const { permissions } = get();
        return permissions.includes(permission);
      },

      hasAnyPermission: (permissions: string[]) => {
        const { permissions: userPermissions } = get();
        return permissions.some(perm => userPermissions.includes(perm));
      },
    }),
    {
      name: "auth-storage",
      partialize: (state) => ({
        user: state.user,
        isAuthenticated: state.isAuthenticated,
        permissions: state.permissions,
      }),
    }
  )
);
